import { createBrowserRouter, Navigate, useRouteError, isRouteErrorResponse, useLocation } from "react-router-dom";
import { Suspense, useEffect, type ReactNode } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { useAppSelector } from "./store";
import lazyWithRetry from "src/utils/lazyWithRetry";
import {
  isChunkLoadError,
  shouldReloadForChunkError,
  markChunkReloadAttempt,
  clearChunkReloadAttempt
} from "src/utils/chunkError";
import { CHUNK_FAILED_MESSAGE, FINANCE_MANAGER, PARTICIPANT, SUPPORTER } from "src/components/constant/constant";

const ROUTE_CHUNK_SCOPE = "route-error";

const fallbackStyle = { padding: 24, textAlign: "center" as const, fontFamily: "sans-serif" };

function ChunkErrorFallback() {
  const error = useRouteError();
  const isChunk = isChunkLoadError(error);
  const alreadyRetried = isChunk && !shouldReloadForChunkError(ROUTE_CHUNK_SCOPE);

  useEffect(() => {
    if (!isChunk) return;

    if (shouldReloadForChunkError(ROUTE_CHUNK_SCOPE)) {
      markChunkReloadAttempt(ROUTE_CHUNK_SCOPE);
      window.location.reload();
    } else {
      clearChunkReloadAttempt(ROUTE_CHUNK_SCOPE);
    }
  }, [isChunk]);

  if (isChunk && !alreadyRetried) {
    return (
      <div
        style={{ ...fallbackStyle, display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
        role="status"
        aria-live="polite"
      >
        <CircularProgress sx={{ color: "#1e293b" }} />
      </div>
    );
  }

  let message = "Something went wrong";

  if (isChunk) {
    message = CHUNK_FAILED_MESSAGE;
  } else if (isRouteErrorResponse(error)) {
    message = error.statusText;
  } else if (error instanceof Error) {
    message = error.message;
  }

  return (
    <div style={fallbackStyle}>
      <h2>Unexpected error</h2>
      <p>{message}</p>
      <button type="button" onClick={() => window.location.reload()}>
        Reload page
      </button>
    </div>
  );
}

const Layout = lazyWithRetry(() => import("src/components/layout/Layout"), "layout");
const Login = lazyWithRetry(() => import("./auth/auth"), "login");
const Users = lazyWithRetry(() => import("src/page/users/Users"), "users");
const Wallet = lazyWithRetry(() => import("src/page/wallets/Wallets"), "wallets");
const Transactions = lazyWithRetry(() => import("src/page/transactions/Transactions"), "transactions");
const UserDetail = lazyWithRetry(() => import("src/page/userDetail/UserDetail"), "user-detail");
const WalletDetail = lazyWithRetry(() => import("src/page/walletDetail/WalletDetail"), "wallet-detail");
const Dashboard = lazyWithRetry(() => import("src/page/dashboard/Dashboard"), "dashboard");
const Guide = lazyWithRetry(() => import("src/page/guide/Guide"), "guide");
const Cards = lazyWithRetry(() => import("./cards/cards"), "cards");
const Organisation = lazyWithRetry(() => import("./organisation/Organisation"), "organisation");
const OrganisationDetail = lazyWithRetry(
  () => import("./organisationDetail/OrganisationDetail"),
  "organisation-detail"
);
const SuperLedgers = lazyWithRetry(() => import("./superLedger/SuperLedgers"), "super-ledgers");
const OnBoarding = lazyWithRetry(() => import("./onBoarding/OnBoarding"), "on-boarding");
const RequestAccess = lazyWithRetry(() => import("./requestAccess/RequestAccess"), "request-access");
const Home = lazyWithRetry(() => import("./home/Home"), "home");
const Activity = lazyWithRetry(() => import("./activity/Activity"), "activity");
const Budget = lazyWithRetry(() => import("./budget/Budget"), "budget");
const NewPayment = lazyWithRetry(() => import("./newPayment/NewPayment"), "new-payment");
const Deposit = lazyWithRetry(() => import("./deposit/Deposit"), "deposit");
const Ledgers = lazyWithRetry(() => import("./ledger/Ledger"), "ledgers");
const InternalPayment = lazyWithRetry(() => import("src/components/newPaymentCards/InternalPay"), "internal-payment");
const InternalUsers = lazyWithRetry(() => import("./internalUser/InternalUsers"), "internal-users");
const InternalDashboard = lazyWithRetry(() => import("./internalDashboard/InternalDashboard"), "internal-dashboard");
const InternalOrganisation = lazyWithRetry(
  () => import("./internalOrganisation/InternalOrganisation"),
  "internal-organisation"
);
const InternalWallet = lazyWithRetry(() => import("./internalWallets/InternalWallets"), "internal-wallets");
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const InternalFile = lazyWithRetry(() => import("./internalFile/InternalFile"), "internal-file");
const InternalCards = lazyWithRetry(() => import("./internalcards/Internalcards"), "internal-cards");
const BulkUpload = lazyWithRetry(() => import("./bulkUpload/BulkUpload"), "bulk-upload");
const BulkUploadDetail = lazyWithRetry(() => import("./bulkUpload/BulkUploadDetail"), "bulk-upload-detail");
const InternalBulkUpload = lazyWithRetry(() => import("./internalBulkUpload/InternalBulkUpload"), "internal-bulk-upload");
const InternalNovattiFiles = lazyWithRetry(
  () => import("./internalBulkUpload/InternalBulkUpload"),
  "internal-novatti-files"
);
const InternalBulkUploadDetail = lazyWithRetry(
  () => import("./internalBulkUpload/InternalBulkUploadDetail"),
  "internal-bulk-upload-detail"
);
// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
const InternalFileContent = lazyWithRetry(
  () => import("./internalFileContent/InternalFileContent"),
  "internal-file-content"
);
const CardSetting = lazyWithRetry(() => import("./cardSetting/cardSetting"), "card-setting");
const InternalTransactions = lazyWithRetry(
  () => import("./internalTransactions/InternalTransactions"),
  "internal-transactions"
);
const Plans = lazyWithRetry(() => import("./plans/Plans"), "plans");
const ChildrenPlans = lazyWithRetry(() => import("./childrenPlans/childrenPlans"), "children-plans");
const Mcc = lazyWithRetry(() => import("src/page/mcc/Mcc"), "mcc");
const ReferralProgram = lazyWithRetry(() => import("./referralProgram/ReferralProgram"), "referral-program");
const UploadFiles = lazyWithRetry(() => import("./uploadFiles/UploadFiles"), "upload-files");
const Reports = lazyWithRetry(() => import("./reports/Reports"), "reports");
const ReportsContent = lazyWithRetry(() => import("./reportsContent/ReportsContent"), "reports-content");
const UserSettings = lazyWithRetry(() => import("./settings/UserSettings"), "user-settings");
const Analytics = lazyWithRetry(() => import("./analytics/Analytics"), "analytics");
const Group = lazyWithRetry(() => import("./group/Group"), "group");
const GroupDetail = lazyWithRetry(() => import("./groupDetail/GroupDetail"), "group-detail");
const AccountingIntegrations = lazyWithRetry(
  () => import("./accountingIntegrations/AccountingIntegrations"),
  "accounting-integrations"
);
const AccountingCodes = lazyWithRetry(() => import("./accountingCodes/AccountingCodes"), "accounting-codes");
const AccountingSettings = lazyWithRetry(
  () => import("./accountingSettings/AccountingSettings"),
  "accounting-settings"
);
const RecurringTransfer = lazyWithRetry(() => import("./recurringTransfer/RecurringTransfer"), "recurring-transfer");
const OrganisationReports = lazyWithRetry(
  () => import("./organisationReports/OrganisationReports"),
  "organisation-reports"
);
const LicenceReports = lazyWithRetry(() => import("./licenceReports/LicenceReports"), "licence-reports");
const LicenceReportsContent = lazyWithRetry(
  () => import("./licenceReportsContent/LicenceReportsContent"),
  "licence-reports-content"
);
const WalletReports = lazyWithRetry(() => import("./walletReports/WalletReports"), "wallet-reports");
const WalletReportsContent = lazyWithRetry(
  () => import("./walletReportsContent/WalletReportsContent"),
  "wallet-reports-content"
);
const InternalReports = lazyWithRetry(() => import("./internalReports/InternalReports"), "internal-reports");
const Expense = lazyWithRetry(() => import("./expense/Expense"), "expense");
const ExpenseDetail = lazyWithRetry(() => import("./expenseDetail/ExpenseDetail"), "expense-detail");
const TransactionDetail = lazyWithRetry(() => import("./transactionDetail/TransactionDetail"), "transaction-detail");
const Entitlement = lazyWithRetry(() => import("./entitlement/Entitlement"), "entitlement");
const InternalOrder = lazyWithRetry(() => import("./internalOrder/InternalOrder"), "internal-order");
const Order = lazyWithRetry(() => import("./stripeTransactionHistory/Order"), "order");
const Approvals = lazyWithRetry(() => import("./approvals/Approvals"), "approvals");
const MyReimbursements = lazyWithRetry(() => import("./myReimbursements/MyReimbursements"), "my-reimbursements");

function XeroRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  if (!params.has("provider")) params.set("provider", "xero");
  if (!params.has("tab")) params.set("tab", "connection");
  return <Navigate to={`/settings/accounting-integrations?${params.toString()}`} replace />;
}

function MyobRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  params.set("provider", "myob");
  if (!params.has("tab")) params.set("tab", "connection");
  return <Navigate to={`/settings/accounting-integrations?${params.toString()}`} replace />;
}

// Intuit redirects back to <hub>/quickbooks?code=…&state=…&realmId=… — carry the
// whole query string through so the callback can forward code + realmId.
function QuickBooksRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  params.set("provider", "quickbooks");
  if (!params.has("tab")) params.set("tab", "connection");
  return <Navigate to={`/settings/accounting-integrations?${params.toString()}`} replace />;
}

// Roles that must not reach the people/card administration pages. Finance
// Managers run the books — transactions, coding and reports — but do not
// administer members or cards; Team Members (and legacy supporters) only ever
// see their own expenses. Hiding the nav item alone is not enough: the routes
// are directly addressable by URL, so gate them here too (QA F-9/F-10,
// 2026-09-22: a Team Member could open /users and /cards by URL).
const USER_AND_CARD_ADMIN_DENIED_ROLES = [FINANCE_MANAGER, PARTICIPANT, SUPPORTER];

function RestrictedRoute({ denyRoles, children }: { denyRoles: string[]; children: ReactNode }) {
  const role = useAppSelector((state) => state.auth.userInfo?.role);
  // Only redirect once the role is known, so a slow profile load never bounces
  // a legitimate user off the page they asked for.
  if (role && denyRoles.includes(role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function MainPage() {
  const menuList = useAppSelector((state) => state.auth.menuList);
  const menuItem = menuList.find((item) => item.id === "HOME" || item.id === "DASHBOARD");

  switch (menuItem?.id) {
    case "HOME":
      return (
        <Suspense>
          <Home />
        </Suspense>
      );
    case "DASHBOARD":
      return (
        <Suspense>
          <Dashboard />
        </Suspense>
      );
    default:
      return null;
  }
}

const router = createBrowserRouter([
  {
    path: "/sign-in",
    element: (
      <Suspense>
        <Login />
      </Suspense>
    ),
    errorElement: <ChunkErrorFallback />
  },
  {
    path: "/auth-sign-in",
    element: (
      <Suspense>
        <Login />
      </Suspense>
    ),
    errorElement: <ChunkErrorFallback />
  },
  {
    path: "/",
    element: (
      <Suspense>
        <Layout />
      </Suspense>
    ),
    errorElement: <ChunkErrorFallback />,
    children: [
      {
        index: true,
        element: (
          <Suspense>
            <MainPage />
          </Suspense>
        )
      },
      {
        path: "/home",
        element: (
          <Suspense>
            <Home />
          </Suspense>
        )
      },
      {
        path: "/guide",
        element: (
          <Suspense>
            <Guide />
          </Suspense>
        )
      },
      {
        path: "/users",
        element: (
          <RestrictedRoute denyRoles={USER_AND_CARD_ADMIN_DENIED_ROLES}>
            <Suspense>
              <Users />
            </Suspense>
          </RestrictedRoute>
        )
      },
      {
        path: "/wallets",
        element: (
          <Suspense>
            <Wallet />
          </Suspense>
        )
      },
      {
        path: "/transactions/:transactionId?",
        element: (
          <Suspense>
            <Transactions />
          </Suspense>
        )
      },
      {
        path: "/accounting/:transactionId",
        element: (
          <Suspense>
            <TransactionDetail />
          </Suspense>
        )
      },
      {
        path: "/users/:userId",
        element: (
          <Suspense>
            <UserDetail />
          </Suspense>
        )
      },
      {
        path: "/wallets/:walletId",
        element: (
          <Suspense>
            <WalletDetail />
          </Suspense>
        )
      },
      {
        path: "/wallets/:walletId/transactions",
        element: (
          <Suspense>
            <Transactions />
          </Suspense>
        )
      },
      {
        path: "/wallets/:walletId/internal-transactions",
        element: (
          <Suspense>
            <InternalTransactions />
          </Suspense>
        )
      },
      {
        path: "/users/:userId/transactions",
        element: (
          <Suspense>
            <Transactions />
          </Suspense>
        )
      },
      {
        path: "/cards",
        element: (
          <RestrictedRoute denyRoles={USER_AND_CARD_ADMIN_DENIED_ROLES}>
            <Suspense>
              <Cards />
            </Suspense>
          </RestrictedRoute>
        )
      },
      {
        path: "/wallets?/:walletId?/deposit",
        element: (
          <Suspense>
            <Deposit />
          </Suspense>
        )
      },
      {
        path: "/account",
        element: (
          <Suspense>
            <Organisation />
          </Suspense>
        )
      },
      {
        path: "/account/:organisationId",
        element: (
          <Suspense>
            <OrganisationDetail />
          </Suspense>
        )
      },
      {
        path: "/account/:organisationId/transactions",
        element: (
          <Suspense>
            <Transactions />
          </Suspense>
        )
      },
      {
        path: "/account/:organisationId/users",
        element: (
          <Suspense>
            <Users />
          </Suspense>
        )
      },
      {
        path: "/account/:organisationId/wallets",
        element: (
          <Suspense>
            <Wallet />
          </Suspense>
        )
      },
      {
        path: "/account/:organisationId/cards",
        element: (
          <Suspense>
            <Cards />
          </Suspense>
        )
      },
      {
        path: "/reports/:reportId?",
        element: (
          <Suspense>
            <Reports />
          </Suspense>
        )
      },
      {
        path: "/account-settings",
        element: (
          <Suspense>
            <UserSettings />
          </Suspense>
        )
      },
      {
        path: "/licence-reports",
        element: (
          <Suspense>
            <LicenceReports />
          </Suspense>
        )
      },
      {
        path: "/licence-reports/:reportId",
        element: (
          <Suspense>
            <LicenceReportsContent />
          </Suspense>
        )
      },
      {
        path: "/wallet-reports",
        element: (
          <Suspense>
            <WalletReports />
          </Suspense>
        )
      },
      {
        path: "/wallet-reports/:reportId",
        element: (
          <Suspense>
            <WalletReportsContent />
          </Suspense>
        )
      },
      {
        path: "/account-reports",
        element: (
          <Suspense>
            <OrganisationReports />
          </Suspense>
        )
      },
      {
        path: "/account-reports/:reportId",
        element: (
          <Suspense>
            <ReportsContent />
          </Suspense>
        )
      },
      {
        path: "/superLedgers",
        element: (
          <Suspense>
            <SuperLedgers />
          </Suspense>
        )
      },
      {
        path: "/activity",
        element: (
          <Suspense>
            <Activity />
          </Suspense>
        )
      },
      {
        path: "/budget",
        element: (
          <Suspense>
            <Budget />
          </Suspense>
        )
      },
      {
        path: "/newPayment",
        element: (
          <Suspense>
            <NewPayment />
          </Suspense>
        )
      },
      {
        path: "/ledgers",
        element: (
          <Suspense>
            <Ledgers />
          </Suspense>
        )
      },
      {
        path: "/transferfunds",
        element: (
          <Suspense>
            <InternalPayment />
          </Suspense>
        )
      },
      {
        path: "/internal",
        element: (
          <Suspense>
            <InternalDashboard />
          </Suspense>
        )
      },
      {
        path: "/internal-users",
        element: (
          <Suspense>
            <InternalUsers />
          </Suspense>
        )
      },
      {
        path: "/internal-users/:userId",
        element: (
          <Suspense>
            <UserDetail />
          </Suspense>
        )
      },
      {
        path: "/internal-account",
        element: (
          <Suspense>
            <InternalOrganisation />
          </Suspense>
        )
      },
      {
        path: "/internal-wallets",
        element: (
          <Suspense>
            <InternalWallet />
          </Suspense>
        )
      },
      {
        path: "/internal-card",
        element: (
          <Suspense>
            <InternalCards />
          </Suspense>
        )
      },
      {
        path: "/internal-transactions",
        element: (
          <Suspense>
            <InternalTransactions />
          </Suspense>
        )
      },
      {
        path: "/internal-files",
        element: (
          <Suspense>
            <InternalBulkUpload />
          </Suspense>
        )
      },
      {
        path: "/internal-novattifiles",
        element: (
          <Suspense>
            <InternalNovattiFiles forcedFileType="NOVATTI_DAILY_TRANSACTION" />
          </Suspense>
        )
      },
      {
        path: "/internal-files/:fileIds/:fileType?",
        element: (
          <Suspense>
            <InternalBulkUploadDetail />
          </Suspense>
        )
      },
      {
        path: "/internal-reports",
        element: (
          <Suspense>
            <InternalReports />
          </Suspense>
        )
      },
      {
        path: "/internal-reports/:reportId",
        element: (
          <Suspense>
            <ReportsContent />
          </Suspense>
        )
      },
      {
        path: "/bulk-upload",
        element: (
          <Suspense>
            <BulkUpload />
          </Suspense>
        )
      },
      {
        path: "/bulk-upload/:fileId",
        element: (
          <Suspense>
            <BulkUploadDetail />
          </Suspense>
        )
      },
      {
        path: "/cardSetting",
        element: (
          <Suspense>
            <CardSetting />
          </Suspense>
        )
      },
      {
        path: "/subscription",
        element: (
          <Suspense>
            <Plans />
          </Suspense>
        )
      },
      {
        path: "/subscription/:subscriptionId",
        element: (
          <Suspense>
            <ChildrenPlans />
          </Suspense>
        )
      },
      {
        path: "/mcc",
        element: (
          <Suspense>
            <Mcc />
          </Suspense>
        )
      },
      {
        path: "/referralProgram",
        element: (
          <Suspense>
            <ReferralProgram />
          </Suspense>
        )
      },
      {
        path: "/uploadFiles",
        element: (
          <Suspense>
            <UploadFiles />
          </Suspense>
        )
      },
      {
        path: "/analytics",
        element: (
          <Suspense>
            <Analytics />
          </Suspense>
        )
      },
      {
        path: "/groups",
        element: (
          <Suspense>
            <Group />
          </Suspense>
        )
      },
      {
        path: "/groups/sites",
        element: (
          <Suspense>
            <Group />
          </Suspense>
        )
      },
      {
        path: "/groups/cost-centres",
        element: (
          <Suspense>
            <Group />
          </Suspense>
        )
      },
      {
        path: "/groups/sites/:groupId",
        element: (
          <Suspense>
            <GroupDetail />
          </Suspense>
        )
      },
      {
        path: "/groups/cost-centres/:groupId",
        element: (
          <Suspense>
            <GroupDetail />
          </Suspense>
        )
      },
      {
        path: "/groups/:groupId",
        element: (
          <Suspense>
            <GroupDetail />
          </Suspense>
        )
      },
      {
        path: "/xero",
        element: <XeroRedirect />
      },
      {
        path: "/myob",
        element: <MyobRedirect />
      },
      {
        path: "/quickbooks",
        element: <QuickBooksRedirect />
      },
      {
        path: "/settings/accounting",
        element: (
          <Suspense>
            <AccountingSettings />
          </Suspense>
        )
      },
      {
        path: "/settings/accounting-integrations",
        element: (
          <Suspense>
            <AccountingIntegrations />
          </Suspense>
        )
      },
      {
        path: "/settings/accounting-codes",
        element: (
          <Suspense>
            <AccountingCodes />
          </Suspense>
        )
      },
      {
        path: "xero/:filterType?/:selectedDays?/transactions",
        element: (
          <Suspense>
            <Transactions />
          </Suspense>
        )
      },
      {
        path: "/recurring-transfer",
        element: (
          <Suspense>
            <RecurringTransfer />
          </Suspense>
        )
      },
      {
        path: "/recurring-transfer/:recurringTransactionId/transactions",
        element: (
          <Suspense>
            <Transactions />
          </Suspense>
        )
      },
      {
        path: "/claims",
        element: (
          <Suspense>
            <Expense />
          </Suspense>
        )
      },
      {
        path: "/claims/:expenseId",
        element: (
          <Suspense>
            <ExpenseDetail />
          </Suspense>
        )
      },
      {
        path: "/claims/:expenseId/transactions",
        element: (
          <Suspense>
            <Transactions />
          </Suspense>
        )
      },
      {
        path: "/approvals",
        element: (
          <Suspense>
            <Approvals />
          </Suspense>
        )
      },
      {
        path: "/my-reimbursement",
        element: (
          <Suspense>
            <MyReimbursements />
          </Suspense>
        )
      },
      {
        path: "/internal-order",
        element: (
          <Suspense>
            <InternalOrder />
          </Suspense>
        )
      },
      {
        path: "order",
        element: (
          <Suspense>
            <Order />
          </Suspense>
        )
      },
      {
        path: "/internal-entitlement",
        element: (
          <Suspense>
            <Entitlement />
          </Suspense>
        )
      }
    ]
  },
  {
    path: "/on-boarding",
    element: (
      <Suspense>
        <OnBoarding />
      </Suspense>
    ),
    errorElement: <ChunkErrorFallback />
  },
  {
    path: "/request-access",
    element: (
      <Suspense>
        <RequestAccess />
      </Suspense>
    ),
    errorElement: <ChunkErrorFallback />
  }
]);

export default router;
